/*
  LLC add-to-cart for custom sections (homepage basket and product rows).
  Mirrors Horizon's product-form batch add: announces a CartLinesUpdateEvent, posts to /cart/add with the
  cart drawer's section ids, then resolves the event with the refreshed cart so the drawer and cart count
  update and the drawer opens (when "auto open" is on). Each button lives in a real <form action="/cart/add">,
  so without JavaScript the browser falls back to a normal add.

  Markup: <form data-llc-add-form> with hidden inputs items[][id] / items[][quantity] (or id / quantity)
  and a submit button.
*/
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

function readItems(form) {
  const data = new FormData(form);
  const ids = data.getAll('items[][id]');
  const quantities = data.getAll('items[][quantity]');
  if (ids.length) {
    return ids.map((id, i) => ({ id: Number(id), quantity: Number(quantities[i]) || 1 }));
  }
  const single = data.get('id');
  return single ? [{ id: Number(single), quantity: Number(data.get('quantity')) || 1 }] : [];
}

async function fetchCart() {
  const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
  return response.json();
}

async function addItems(form, items) {
  const button = form.querySelector('[type="submit"]');
  const status = form.querySelector('[data-llc-add-status]');
  const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    })
  );

  if (button) button.setAttribute('aria-busy', 'true');

  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items, sections: sectionIds.join(',') }),
    }).then((r) => r.json());

    const cart = await fetchCart();
    const didError = Boolean(response.status);

    if (didError) {
      form.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
      if (status) status.textContent = response.description || response.message || 'Sorry, that could not be added.';
    } else if (status) {
      status.textContent = totalQuantity > 1 ? `${totalQuantity} items added to your cart.` : 'Added to your cart.';
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'llc-add-form',
        sourceId: form.id || 'llc-add-form',
        itemCount: totalQuantity,
        sections: didError ? undefined : response.sections,
        didError,
      },
    });
  } catch (error) {
    deferred.reject(error);
    form.dispatchEvent(new CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
    // Fall back to a normal form post so the add still happens.
    form.submit();
  } finally {
    if (button) button.removeAttribute('aria-busy');
  }
}

if (!window.llcCartReady) {
  window.llcCartReady = true;
  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('form[data-llc-add-form]');
    if (!form) return;
    const items = readItems(form);
    if (!items.length) return;
    event.preventDefault();
    addItems(form, items);
  });
}
